import * as React from "react";
import { Eye, Monitor, Smartphone } from "lucide-react";

import { Button, cn } from "@ando/ui";

/**
 * OrgBrandingPreview — live preview of the login page using the
 * in-edit branding state.
 *
 * v1 reference: ``ando-frontend/app/admin-v2/components/pages/
 * organizations/components/OrgBrandingPreview.tsx`` (146 LoC). Same
 * two-device toggle (desktop / mobile), same deferred image URL
 * (defers logo URL only — colors + name update instantly so admins
 * can iterate without flooding the network panel with 404s for
 * half-typed URLs).
 *
 * Pure presentational: consumers feed the current form state via
 * props. CSS vars approach is intentional — keeps the preview
 * isolated from the running app theme.
 */

const DEFAULT_BRAND_PRIMARY = "#ff6f3c";
const DEFAULT_BRAND_ACCENT = "#fff4ee";

export interface OrgBrandingPreviewProps {
  brandName: string;
  brandPrimaryColor: string;
  brandAccentColor: string;
  brandLogoUrl: string;
  /** Fallback name when ``brandName`` is empty — typically the org name. */
  fallbackName: string;
}

function ensureHexHash(input: string, fallback: string): string {
  if (!input) return fallback;
  if (input.startsWith("#")) return input;
  if (/^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{6}$/.test(input)) return `#${input}`;
  return fallback;
}

export function OrgBrandingPreview({
  brandName,
  brandPrimaryColor,
  brandAccentColor,
  brandLogoUrl,
  fallbackName,
}: OrgBrandingPreviewProps): React.ReactElement {
  const [device, setDevice] = React.useState<"desktop" | "mobile">("desktop");

  // Defer the logo URL so partial-URL keystrokes don't trigger a
  // request-per-character. Colors + name update synchronously.
  const deferredLogoUrl = React.useDeferredValue(brandLogoUrl);

  const accent = ensureHexHash(brandAccentColor, DEFAULT_BRAND_ACCENT);
  const primary = ensureHexHash(brandPrimaryColor, DEFAULT_BRAND_PRIMARY);
  const displayName = brandName || fallbackName;

  return (
    <section
      aria-labelledby="org-branding-preview"
      className="rounded-lg border border-hairline bg-card/55 p-5"
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <h2
            id="org-branding-preview"
            className="inline-flex items-center gap-1.5 font-semibold text-[10px] text-ink-3 uppercase tracking-wide"
          >
            <Eye size={12} aria-hidden /> Anteprima pagina di login
          </h2>
          <p className="mt-1 text-[12px] text-ink-3">
            Aggiornata in tempo reale mentre modifichi colori e logo.
          </p>
        </div>
        <div
          role="group"
          aria-label="Anteprima dispositivo"
          className="inline-flex rounded-md border border-hairline p-0.5"
        >
          <Button
            type="button"
            variant={device === "desktop" ? "outline" : "ghost"}
            size="sm"
            className="h-7 px-2"
            onClick={() => setDevice("desktop")}
            aria-pressed={device === "desktop"}
            aria-label="Anteprima desktop"
          >
            <Monitor className="h-3.5 w-3.5" aria-hidden />
          </Button>
          <Button
            type="button"
            variant={device === "mobile" ? "outline" : "ghost"}
            size="sm"
            className="h-7 px-2"
            onClick={() => setDevice("mobile")}
            aria-pressed={device === "mobile"}
            aria-label="Anteprima mobile"
          >
            <Smartphone className="h-3.5 w-3.5" aria-hidden />
          </Button>
        </div>
      </div>
      <div
        className={cn(
          "mx-auto overflow-hidden rounded-lg border border-hairline transition-all duration-200",
          device === "desktop"
            ? "h-[300px] w-full max-w-[420px]"
            : "h-[360px] w-[220px]",
        )}
        style={{ backgroundColor: accent }}
        role="img"
        aria-label={`Anteprima pagina di login per ${displayName}`}
      >
        <div className="flex h-full flex-col items-center justify-center p-4">
          <div className="w-full max-w-[260px] rounded-xl bg-white p-5 shadow-md">
            <div className="mb-4 flex justify-center">
              {deferredLogoUrl ? (
                // Plain <img> — we intentionally do NOT fall back to
                // Next/Image since the v2 client uses Vite. Errors are
                // swallowed (broken URL → empty box) to avoid breaking
                // the preview when the admin is mid-typing.
                <img
                  src={deferredLogoUrl}
                  alt=""
                  className="h-8 object-contain"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.visibility =
                      "hidden";
                  }}
                />
              ) : (
                <div className="flex h-8 w-24 items-center justify-center rounded bg-cream-deep/60 text-[10px] text-ink-3">
                  Logo
                </div>
              )}
            </div>
            <h3 className="mb-3 text-center font-semibold text-[12.5px] text-ink-1">
              {displayName}
            </h3>
            <div className="mb-3 space-y-2">
              <div className="rounded border border-hairline px-2 py-1.5 text-[10px] text-ink-3">
                user@example.com
              </div>
              <div className="rounded border border-hairline px-2 py-1.5 text-[10px] text-ink-3">
                ••••••••
              </div>
            </div>
            <button
              type="button"
              disabled
              className="w-full rounded py-1.5 font-medium text-[11px] text-white"
              style={{ backgroundColor: primary }}
            >
              Accedi
            </button>
            <p
              className="mt-2 text-center text-[10px]"
              style={{ color: primary }}
            >
              Password dimenticata?
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
